/**
 * Plain-language mapping for planning-engine outcomes (C Mode, Doc 26 rule 5).
 *
 * The engine speaks workbook vocabulary ("Out - capacity",
 * "Blocked - Capability"). C-Mode surfaces speak consumer language. This is
 * the single mapping both the feasibility dashboard and scenario comparison
 * use, per SPEC-02 §5 (docs/9-exploration/c-mode/).
 */

import { getDecisionReason, getPriorityDecisionReason } from './planningEngineModule';
import type {
  DecisionWithParticipation,
  PlanningEngineOutput,
  PriorityFeasibility,
  PrioritySummary,
} from './planningEngineModule.types';

// ---------------------------------------------------------------------------
// Decision → phrase
// ---------------------------------------------------------------------------

export const DECISION_PLAIN_LANGUAGE: Record<DecisionWithParticipation, string> = {
  'Included': 'Fits the plan',
  'At Risk - Participation': "Fits, but a required team hasn't confirmed",
  'At Risk - Capacity': "Fits, but participation capacity isn't confirmed",
  'Blocked - Capability': 'A required skill is missing',
  'Out - capacity': 'Capacity runs out before this one',
  'Blocked': 'Waiting on something else first',
  'Not Ready': 'Not sized yet — needs an estimate',
  'No Capacity Model': 'No team capacity defined for this kind of work',
  'No Work Units Defined': 'Not broken into work yet',
};

export const FEASIBILITY_PLAIN_LANGUAGE: Record<PriorityFeasibility, string> = {
  'Fully Achievable': 'Deliverable',
  'Partially Achievable': 'Partially deliverable',
  'Not Achievable': 'Not deliverable as planned',
  'No Work': 'No work identified',
};

export type FeasibilityTone = 'green' | 'amber' | 'red' | 'neutral';

export function feasibilityTone(f: PriorityFeasibility): FeasibilityTone {
  switch (f) {
    case 'Fully Achievable': return 'green';
    case 'Partially Achievable': return 'amber';
    case 'Not Achievable': return 'red';
    case 'No Work': return 'neutral';
  }
}

export function decisionTone(d: DecisionWithParticipation): FeasibilityTone {
  if (d === 'Included') return 'green';
  if (d === 'At Risk - Participation' || d === 'At Risk - Capacity' || d === 'Out - capacity') return 'amber';
  if (d === 'Blocked' || d === 'Blocked - Capability') return 'red';
  return 'neutral';
}

// ---------------------------------------------------------------------------
// Priority outcome rows (one per priority — the C-Mode above/below list)
// ---------------------------------------------------------------------------

/** The engine's wording for an ordinary unsatisfied dependency — not specific enough to show as the headline. */
const GENERIC_BLOCKED_REASONS: ReadonlySet<string> = new Set([
  getDecisionReason('Blocked'),
  getPriorityDecisionReason('Blocked'),
]);

export interface PriorityOutcome {
  summary: PrioritySummary;
  /** True when the priority lands above the line (fully or partially). */
  aboveLine: boolean;
  /** Consumer-facing one-liner for this priority's outcome. */
  plainReason: string;
  /** The engine's own decision reason, for the expandable detail. */
  engineReason: string | null;
}

/**
 * Roll the engine output up to one consumer-readable row per priority,
 * in the order capacity was consumed (rank order).
 */
export function buildPriorityOutcomes(output: PlanningEngineOutput): PriorityOutcome[] {
  const reasonByPriority = new Map<string, string>();
  for (const rec of output.priorityPlanRecords) {
    if (!reasonByPriority.has(rec.priority.id)) {
      reasonByPriority.set(rec.priority.id, rec.decisionReason);
    }
  }
  // Work-unit grain: surface the first non-included record's reason.
  for (const rec of output.planRecords) {
    const pid = rec.priority?.id;
    if (!pid || reasonByPriority.has(pid)) continue;
    if (rec.decisionWithParticipation !== 'Included') {
      reasonByPriority.set(pid, rec.decisionReason);
    }
  }

  return output.prioritySummaries.map((summary) => {
    const aboveLine =
      summary.feasibility === 'Fully Achievable' || summary.feasibility === 'Partially Achievable';
    // A package, competing set or duplicate that stopped this priority says so
    // in its own words; "waiting on something else" would hide which rule it was.
    const groupReason =
      summary.blockedReason && !GENERIC_BLOCKED_REASONS.has(summary.blockedReason)
        ? summary.blockedReason
        : null;
    const plainReason = groupReason
      ? groupReason
      : summary.hasParticipationRisk
      ? DECISION_PLAIN_LANGUAGE[summary.decisionWithParticipation]
      : aboveLine
        ? summary.feasibility === 'Fully Achievable'
          ? 'Fits the plan'
          : `Only ${summary.coveragePercent}% of its work fits`
        : DECISION_PLAIN_LANGUAGE[summary.decisionWithParticipation] ??
          FEASIBILITY_PLAIN_LANGUAGE[summary.feasibility];
    return {
      summary,
      aboveLine,
      plainReason,
      engineReason: reasonByPriority.get(summary.priority.id) ?? null,
    };
  });
}

// ---------------------------------------------------------------------------
// Levers ("what would change the answer") — SPEC-02 §3.1
// ---------------------------------------------------------------------------

export interface FeasibilityLever {
  key: string;
  count: number;
  label: string;
  detail: string;
  /** B-Mode escape hatch (Doc 26 §5). */
  href: string;
  linkLabel: string;
}

/** Derive the levers list from an engine run. Levers with zero count are pruned (absent, not disabled). */
export function buildFeasibilityLevers(output: PlanningEngineOutput): FeasibilityLever[] {
  const exec = output.executiveSummary;
  const levers: FeasibilityLever[] = [
    {
      key: 'capacity',
      count: exec.deferredCount,
      label: 'Add capacity or change the order',
      detail: `${exec.deferredCount} item${exec.deferredCount === 1 ? '' : 's'} fit the plan logically but ran out of capacity.`,
      href: '/planning/engine/capacity',
      linkLabel: 'Open Resource Budget',
    },
    {
      key: 'participation',
      count: exec.participationAtRiskCount + exec.participationBlockedCount,
      label: 'Confirm team participation',
      detail: `${exec.participationAtRiskCount + exec.participationBlockedCount} item${exec.participationAtRiskCount + exec.participationBlockedCount === 1 ? '' : 's'} need a supporting team to confirm capability, capacity, or commitment.`,
      href: '/planning/engine/participation',
      linkLabel: 'Open Participation',
    },
    {
      key: 'dependencies',
      count: exec.blockedCount,
      label: 'Resolve what they wait on',
      detail: `${exec.blockedCount} item${exec.blockedCount === 1 ? '' : 's'} are blocked by a dependency or readiness constraint.`,
      href: '/planning/engine/dependencies',
      linkLabel: 'Open Dependencies',
    },
    {
      key: 'estimates',
      count: exec.notReadyCount,
      label: 'Size the unsized work',
      detail: `${exec.notReadyCount} item${exec.notReadyCount === 1 ? '' : 's'} have no demand estimate yet, so the engine can't place them.`,
      href: '/planning/engine/priorities',
      linkLabel: 'Open Priorities',
    },
    {
      key: 'capacity-model',
      count: exec.noCapacityModelCount,
      label: 'Define capacity for missing pools',
      detail: `${exec.noCapacityModelCount} item${exec.noCapacityModelCount === 1 ? '' : 's'} need a kind of work that has no capacity pool defined.`,
      href: '/planning/engine/capacity',
      linkLabel: 'Open Resource Budget',
    },
  ];
  return levers.filter((l) => l.count > 0);
}

/** Consumer-facing reason for a priority below the planning line — separates deferred vs blocked. */
export function belowLineReasonText(s: PrioritySummary): string {
  const parts: string[] = [];
  const resource = s.priority.priorityResourceType ?? 'engineering';

  if (s.deferredWU > 0) {
    parts.push(`Deferred (${s.deferredWU.toFixed(1)} WU) — insufficient ${resource} capacity`);
  }
  if (s.blockedOrNotReadyWU > 0) {
    if (s.decisionWithParticipation === 'Blocked') {
      parts.push(`Blocked (${s.blockedOrNotReadyWU.toFixed(1)} WU) — waiting on a dependency`);
    } else if (s.decisionWithParticipation === 'Not Ready') {
      parts.push(`Not ready (${s.blockedOrNotReadyWU.toFixed(1)} WU) — work unit not in a plannable state`);
    } else {
      parts.push(`Blocked or not ready (${s.blockedOrNotReadyWU.toFixed(1)} WU)`);
    }
  }
  if (s.feasibility === 'No Work') {
    return 'Not sized yet — needs an estimate before capacity can be checked';
  }
  if (parts.length > 0) return parts.join(' · ');
  return DECISION_PLAIN_LANGUAGE[s.decisionWithParticipation] ?? 'Constraint not met';
}
